import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { trackEvent } from '../utils/analytics';
import '../styles/landing.css';
import { Header } from './Header';
import { Hero } from './Hero';
import { ResultsStrip } from './ResultsStrip';
import { UseCases } from './UseCases';
import { BuildSimple } from './BuildSimple';
import { LiveActivations } from './LiveActivations';
import { GameShowcase } from './GameShowcase';
import { Pricing } from './Pricing';
import { FAQ } from './Faqs';
import { Footer } from './Footer';
import { SignupModalProvider } from './SignupModalContext';

const LANDING_BG = '#3B4F5E';
const canonicalUrl = 'https://www.smilesflow.com';
const ogImage = `${canonicalUrl}/og-image.png`;

const LANDING_TITLE = 'SmilesFlow | Branded Mini-Games That Capture Leads';
const LANDING_DESCRIPTION = 'Launch a branded arcade game in minutes. Share via QR code, link, or embed. Players compete on a live leaderboard while you track plays, entries, and CTA clicks.';

const STRUCTURED_DATA_ORGANIZATION = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'SmilesFlow',
  url: 'https://www.smilesflow.com',
  logo: 'https://www.smilesflow.com/logo.png',
  sameAs: ['https://twitter.com/SmilesFlowX'],
};

const STRUCTURED_DATA_SOFTWARE = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'SmilesFlow',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: 'Self-serve branded mini-game campaign software with live leaderboards, QR codes, campaign links, branded CTAs, lead capture, and results tracking.',
  offers: [
    { '@type': 'Offer', price: '0', priceCurrency: 'GBP', description: 'Free for your first published campaign. Full feature set with a small "Powered by SmilesFlow" badge.' },
    { '@type': 'Offer', price: '39', priceCurrency: 'GBP', description: 'Publish 1 branded game. Full feature set, badge removed.' },
    { '@type': 'Offer', price: '149', priceCurrency: 'GBP', description: '5-game pack - same full feature set on every game. Best for repeat campaigns.' },
  ],
  url: 'https://www.smilesflow.com',
};

const STRUCTURED_DATA_FAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'How does pricing work?', acceptedAnswer: { '@type': 'Answer', text: 'Your first campaign is free with a small "Powered by SmilesFlow" badge. £39 publishes another game and removes the badge. £149 for a 5-game pack. No subscription, no card needed to start.' } },
    { '@type': 'Question', name: 'What does £39 unlock?', acceptedAnswer: { '@type': 'Answer', text: 'A second (or third, etc.) live campaign and a brand-clean output - the SmilesFlow badge is removed. Every campaign already includes shareable link, QR code, live leaderboard, full analytics, CTA tracking, and lead capture, free or paid.' } },
    { '@type': 'Question', name: 'Can I use SmilesFlow for client campaigns?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Build a branded game for one client, share it by link or QR code, then reuse the campaign structure for the next client, launch, or activation.' } },
    { '@type': 'Question', name: 'Can the game use my client branding?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. The game carries your client logo, colours, CTA, and campaign copy. SmilesFlow attribution stays minimal; full white-label is not part of the current package.' } },
    { '@type': 'Question', name: 'Can I edit a game after I publish it?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, free forever. The same public URL keeps working - re-publishing updates the live game in place and never charges another credit.' } },
    { '@type': 'Question', name: 'Can I use this on Instagram?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. Publish the game, copy the link, and use it in a story, bio, DM, email, or post-click campaign. Players open it in their phone browser.' } },
    { '@type': 'Question', name: 'Do players need to download an app?', acceptedAnswer: { '@type': 'Answer', text: 'No. Players open the game link or scan a QR code and play instantly in their phone browser. No app, no player account.' } },
    { '@type': 'Question', name: 'What games are available?', acceptedAnswer: { '@type': 'Answer', text: 'Nine formats: Snake, Lane Dash, Jump Runner, Sky Bounce, Flap & Fly, Quiz Rush, Complete the List, Minigolf, and Stacker. All carry your logo as the in-game collectible.' } },
  ],
};

const SCROLL_OFFSET_PX = 88; // Fixed header height + small gap so section heading is visible

export default function Landing() {
  useEffect(() => {
    document.body.style.backgroundColor = LANDING_BG;
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  // Capture ?ref=<game-id> from "Made with SmilesFlow" badge clicks. Persist
  // to localStorage so signup can attribute the source game later, and ping
  // analytics for immediate visibility into which games drive landing visits.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref) return;
    try {
      localStorage.setItem('sf_ref_game', ref);
      localStorage.setItem('sf_ref_game_at', String(Date.now()));
    } catch { /* storage may be unavailable; non-fatal */ }
    trackEvent('made_with_ref_visit', { ref });
  }, []);

  // Scroll anchor links with offset so the sticky header doesn't cover section headings.
  useEffect(() => {
    const handleClick = (e) => {
      const a = e.target.closest('a');
      if (!a || !a.hash) return;
      const id = a.hash.slice(1);
      const el = document.getElementById(id);
      if (!el) return;
      if (a.hostname && a.hostname !== window.location.hostname) return;
      if (a.pathname && a.pathname !== '' && a.pathname !== window.location.pathname) return;
      e.preventDefault();
      let scrollContainer = el.parentElement;
      while (scrollContainer && scrollContainer !== document.body) {
        const cy = getComputedStyle(scrollContainer).overflowY;
        if (cy === 'auto' || cy === 'scroll' || cy === 'overlay') {
          break;
        }
        scrollContainer = scrollContainer.parentElement;
      }
      const targetTop = el.getBoundingClientRect().top - SCROLL_OFFSET_PX;
      if (scrollContainer && scrollContainer !== document.body) {
        scrollContainer.scrollBy({ top: targetTop, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET_PX, behavior: 'smooth' });
      }
      if (window.history.pushState) window.history.pushState(null, '', a.hash);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  return (
    <SignupModalProvider>
    <div className="landing-page min-h-screen flex flex-col bg-gch-bg text-white antialiased scroll-smooth">
      <Helmet>
        <title>{LANDING_TITLE}</title>
        <meta name="description" content={LANDING_DESCRIPTION} />
        <meta name="keywords" content="smilesflow, branded mini games, branded game campaign, instagram story game, qr game, live leaderboard, lead capture game, branded arcade games, product launch game, creator campaign game, agency campaign game" />
        <meta name="author" content="SmilesFlow" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={LANDING_TITLE} />
        <meta property="og:description" content={LANDING_DESCRIPTION} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="SmilesFlow" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@SmilesFlowX" />
        <meta name="twitter:creator" content="@SmilesFlowX" />
        <meta name="twitter:title" content={LANDING_TITLE} />
        <meta name="twitter:description" content={LANDING_DESCRIPTION} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content="SmilesFlow" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA_ORGANIZATION) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA_SOFTWARE) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA_FAQ) }} />
      </Helmet>
      <Header />
      <Hero />
      <ResultsStrip />
      <main className="relative">
        <UseCases />
        <BuildSimple />
        <GameShowcase />
        <LiveActivations />
        <Pricing />
        <FAQ />
      </main>
      <Footer />
    </div>
    </SignupModalProvider>
  );
}
